import { Badge } from "./ui/badge";

/**
 * Interactive body outline (front view only).
 * Tappable anatomical regions toggle membership in `selected`.
 */
export const BODY_REGIONS = ["Scalp", "Face", "Neck", "Chest", "Back", "Arms", "Hands", "Legs", "Feet"];

export default function BodyMap({ selected = [], onToggle }) {
  const isOn = (r) => selected.includes(r);
  const fill = (r) => (isOn(r) ? "#2F9A3B" : "#E8F2E2");
  const stroke = (r) => (isOn(r) ? "#1A5A22" : "#A8C19B");

  const regionProps = (r) => ({
    fill: fill(r),
    stroke: stroke(r),
    strokeWidth: 1.5,
    onClick: () => onToggle(r),
    style: { cursor: "pointer", transition: "fill 200ms ease, stroke 200ms ease" },
    "data-testid": `body-region-${r.toLowerCase()}`,
  });

  return (
    <div data-testid="body-map">
      <figure className="flex flex-col items-center max-w-[220px] mx-auto">
        <svg viewBox="0 0 200 420" className="w-full h-auto" role="img" aria-label="Body diagram">
          {/* Scalp — top sliver of the head only */}
          <path d="M 77 30 A 30 32 0 0 1 123 30 Z" {...regionProps("Scalp")} />
          {/* Face — the rest of the head */}
          <path d="M 77 30 A 30 32 0 1 0 123 30 Z" {...regionProps("Face")} />
          {/* Neck */}
          <rect x="88" y="80" width="24" height="14" rx="3" {...regionProps("Neck")} />
          {/* Chest (front torso) */}
          <path d="M 60 96 Q 60 90 70 90 L 130 90 Q 140 90 140 96 L 140 210 Q 140 220 130 220 L 70 220 Q 60 220 60 210 Z" {...regionProps("Chest")} />
          {/* Left arm */}
          <path d="M 38 100 Q 36 96 42 94 L 56 96 L 58 220 Q 58 226 52 226 L 40 224 Q 36 222 36 216 Z" {...regionProps("Arms")} />
          {/* Right arm */}
          <path d="M 162 100 Q 164 96 158 94 L 144 96 L 142 220 Q 142 226 148 226 L 160 224 Q 164 222 164 216 Z" {...regionProps("Arms")} />
          {/* Left hand */}
          <ellipse cx="47" cy="238" rx="13" ry="15" {...regionProps("Hands")} />
          {/* Right hand */}
          <ellipse cx="153" cy="238" rx="13" ry="15" {...regionProps("Hands")} />
          {/* Left leg */}
          <path d="M 72 222 L 96 222 L 96 348 Q 96 354 90 354 L 76 354 Q 70 354 70 348 Z" {...regionProps("Legs")} />
          {/* Right leg */}
          <path d="M 104 222 L 128 222 L 130 348 Q 130 354 124 354 L 110 354 Q 104 354 104 348 Z" {...regionProps("Legs")} />
          {/* Left foot */}
          <ellipse cx="83" cy="372" rx="16" ry="11" {...regionProps("Feet")} />
          {/* Right foot */}
          <ellipse cx="117" cy="372" rx="16" ry="11" {...regionProps("Feet")} />
        </svg>
      </figure>

      {/* Selected regions summary */}
      <div className="mt-5 flex flex-wrap gap-2 items-center justify-center min-h-[28px]" data-testid="body-map-selected">
        {selected.length === 0 ? (
          <p className="text-xs text-stone-400 italic">Tap any area on the diagram to mark it.</p>
        ) : (
          selected.map((r) => (
            <Badge key={r} onClick={() => onToggle(r)} className="bg-sage-100 text-sage-700 border-0 rounded-full font-medium cursor-pointer hover:bg-sage-200 transition-colors" data-testid={`body-map-chip-${r.toLowerCase()}`}>
              {r} <span className="ml-1 opacity-50">×</span>
            </Badge>
          ))
        )}
      </div>
    </div>
  );
}
